import { useEffect, useState } from "react";
import { Upload, Button, Typography, UploadFile, Flex, UploadProps, GetProp, Col, Skeleton } from "antd";
import { ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import styles from "./fileInput.module.css";
import addFileIcon from "images/addFileIcon.svg";
import { DocUploadedResponse, NOTIFICATION_TYPE_ENUM, S3FileType } from "interfaces";
import { uploadFile } from "services";
import { MAX_FILE_SIZE_MB } from "utils/envConstants";
import { dateFormat, formatFileInfo, getFileIcon, getFileSize } from "utils";
import { useNotificationContext } from "providers";
import { RcFile } from "antd/lib/upload";
import { operations } from "types/openapi";
import { TrashCanButton } from "ui";
import { filesToFormData } from "./helpers";

interface IProps extends Omit<UploadProps, "accept"> {
  accept?: string[];
  blockTitle?: string | React.ReactNode;
  blockDescription?: string | React.ReactNode;
  handleUpload?: (files: UploadFile[], fileType: S3FileType) => Promise<DocUploadedResponse[]>;
  onUpload?: (fileId: DocUploadedResponse[], files?: RcFile[]) => Promise<any>;
  isLocal?: (files?: RcFile[]) => any;
  fileType?: S3FileType;
  multiple?: boolean;
  isContinue?: boolean;
  addMoreText?: string;
  smallSize?: boolean;
}

export type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

export const FileInput = (props: IProps) => {
  const { accept, onUpload, blockTitle, fileType, isLocal, multiple = false, blockDescription } = props;
  const isContinueUpload = props.isContinue;
  const { openNotification } = useNotificationContext();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const fileSizeMessage = `Размер файла не должен превышать ${MAX_FILE_SIZE_MB} МБ`;
  const totalFileSizeMessage = `Общий размер файлов не должен превышать ${MAX_FILE_SIZE_MB} МБ`;
  const maxFileSize = MAX_FILE_SIZE_MB * 1000 * 1000;

  const clearErrors = () => {
    setErrors([]);
  };

  const handleLocalUpload = (filesList = files) => {
    if (isLocal && filesList.length > 0) {
      isLocal(filesList.map((file) => file.originFileObj).filter(Boolean) as RcFile[]);
    }
  };

  const isFileSizeValid = (file: UploadFile): boolean => {
    return !file.size || file.size <= maxFileSize;
  };

  const isFileFormatValid = (file: UploadFile): boolean => {
    if (!accept) return true;
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    return accept.includes(`.${fileExtension}`);
  };

  const isTotalFileSizeValid = (): boolean => {
    const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);
    return totalSize <= maxFileSize;
  };

  const hasFilesWithErrors = (): boolean => {
    return files.some((file) => !isFileSizeValid(file) || !isFileFormatValid(file)) || !isTotalFileSizeValid();
  };

  const handleUpload = async () => {
    if (!(files.length > 0 && fileType)) return;

    if (hasFilesWithErrors()) {
      openNotification({
        description: "Некоторые файлы не прошли проверку",
        type: NOTIFICATION_TYPE_ENUM.ERROR,
      });
      return;
    }

    clearErrors();
    setLoading(true);
    try {
      let s3Ids: DocUploadedResponse[] = [];

      if (props.handleUpload) {
        s3Ids = await props.handleUpload(files, fileType);
      } else {
        s3Ids = await uploadFile(filesToFormData(files, fileType));
      }

      if (onUpload) {
        await onUpload(s3Ids, files.map((file) => file.originFileObj).filter(Boolean) as RcFile[]);
      }
      setFiles([]);

      if (!props.handleUpload) {
        openNotification({
          message: "Файл успешно загружен",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
      }
    } catch (e: any) {
      const errorMessage = e.title || "Ошибка загрузки файла";
      openNotification({
        description: errorMessage,
        type: NOTIFICATION_TYPE_ENUM.ERROR,
      });
      setErrors((prevErrors) => [...prevErrors, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = ({ file: newFile, fileList }: { file: UploadFile; fileList: UploadFile[] }) => {
    if (multiple) {
      setFiles(fileList);
    } else {
      setFiles(fileList.slice(-1));
    }

    if (newFile.size && newFile.size > maxFileSize) {
      openNotification({
        description: fileSizeMessage,
        type: NOTIFICATION_TYPE_ENUM.ERROR,
      });
    } else {
      const updatedFiles = multiple ? fileList : fileList.slice(-1);
      const totalSize = updatedFiles.reduce((sum, file) => sum + (file.size || 0), 0);

      if (totalSize > maxFileSize) {
        openNotification({
          description: totalFileSizeMessage,
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      } else {
        handleLocalUpload(updatedFiles);
      }
    }
  };

  const handleRemove = (file: UploadFile) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.uid !== file.uid));

    if (files.length <= 1) {
      clearErrors();
      setLoading(false);
    }
    return true;
  };

  const definesFileTypes = accept?.join(",") ?? undefined;

  const addMoreText = props.addMoreText
    ? props.addMoreText
    : props.smallSize
      ? `${isContinueUpload ? "Добавить еще" : "Добавить"}`
      : "Добавить ещё";

  useEffect(() => {
    files.length > 0 && handleUpload();
  }, [files.length]);

  return (
    <>
      <Flex className={`${errors.length > 0 ? styles.errorBorder : ""} ${props.className ?? ""}`} vertical>
        {(blockTitle || blockDescription) && (
          <Flex vertical gap="small" className="margin-bottom-md">
            {typeof blockTitle === "string" ? (
              <Typography.Text strong className={styles.blockTitle}>
                {blockTitle}
              </Typography.Text>
            ) : (
              blockTitle
            )}
            {typeof blockDescription === "string" ? (
              <Typography.Text>{blockDescription}</Typography.Text>
            ) : (
              blockDescription
            )}
          </Flex>
        )}
        {files.length > 0 && (
          <Flex className="full-width" vertical gap="small">
            {files.map((file) => (
              <Flex key={file.uid} justify="space-between" className={styles.uploadedFileWrapper} gap="small">
                <Flex gap="small" align="center">
                  <div className="square bg-basic-200 icon-3xl">
                    {isFileSizeValid(file) ? (
                      getFileIcon(file.name)
                    ) : (
                      <ExclamationCircleFilled className={`${styles.fileStatusIcon} ${styles.fileStatusErrorIcon}`} />
                    )}
                  </div>
                  <Flex vertical>
                    <Typography.Text>{file.name}</Typography.Text>
                    <Typography.Text className="text-hint">
                      {file.size &&
                        file.lastModifiedDate &&
                        formatFileInfo({ fileSize: file.size, uploadedAt: file.lastModifiedDate })}
                    </Typography.Text>
                  </Flex>
                </Flex>
                <TrashCanButton className={styles.fileStatusIcon} onRemove={() => handleRemove(file)} />
              </Flex>
            ))}
          </Flex>
        )}
        {(files.length === 0 || multiple) && (
          <Upload
            {...props}
            beforeUpload={(file) => {
              const fileExtension = file.name.split(".").pop()?.toLowerCase();
              const isFormatValid = !accept || accept.includes(`.${fileExtension}`);

              if (!isFormatValid) {
                openNotification({
                  description: `Недопустимый формат файла: ${file.name}`,
                  type: NOTIFICATION_TYPE_ENUM.ERROR,
                });
                return Upload.LIST_IGNORE;
              }

              if (file.size && file.size > maxFileSize) {
                openNotification({
                  description: fileSizeMessage,
                  type: NOTIFICATION_TYPE_ENUM.ERROR,
                });
                return Upload.LIST_IGNORE;
              }

              return false;
            }}
            accept={definesFileTypes}
            onChange={handleChange}
            fileList={files}
            showUploadList={false}
            maxCount={multiple ? undefined : 1}
            multiple={multiple}
            className="full-width"
          >
            <Flex vertical>
              {files.length === 0 && !isContinueUpload && !props.smallSize && (
                <Flex
                  vertical
                  align="center"
                  gap="small"
                  className={`${styles.fileInputWrapper} ${styles.fileInputWrapperColored}`}
                >
                  <Button type="text" icon={<img src={addFileIcon} alt="add file" />} />
                  <Flex vertical align="center">
                    <span>
                      <Typography.Text className="form-text-color">Перетащите </Typography.Text>
                      <Typography.Text className="text-link test-middle">
                        или нажмите для загрузки файл{multiple ? "ов" : "а"}
                      </Typography.Text>
                    </span>
                    <Typography.Text className="form-text-color">до {MAX_FILE_SIZE_MB} МБ</Typography.Text>
                  </Flex>
                  <Typography.Text className="form-text-color">{definesFileTypes}</Typography.Text>
                </Flex>
              )}
              {((multiple && files.length > 0) || isContinueUpload || props.smallSize) && (
                <Flex
                  align="center"
                  className={`${styles.fileInputWrapper} ${styles.fileInputWrapperMultiple} ${files.length > 0 ? "margin-top-sm" : ""}`}
                >
                  <Flex align="center" gap="4px">
                    <PlusOutlined className={styles.plusIcon} />
                    <Typography.Text className="form-text-color">{addMoreText} </Typography.Text>
                  </Flex>
                </Flex>
              )}
            </Flex>
          </Upload>
        )}
        {/* {files.length > 0 && !(hasFilesWithErrors() || isLocal) && (
      <Col className="margin-top-sm">
        <Button type="default" onClick={handleUpload} loading={loading} size="small">
          Отправить
        </Button>
      </Col>
    )} */}

        <Flex vertical className="margin-top-xs">
          {multiple
            ? !isTotalFileSizeValid() && <Typography.Text type="danger">{totalFileSizeMessage}</Typography.Text>
            : files.some((file) => !isFileSizeValid(file)) && (
                <Typography.Text type="danger">{fileSizeMessage}</Typography.Text>
              )}
          {errors.length > 0 &&
            errors.map((error, index) => (
              <Typography.Text type="danger" key={index}>
                {error}
              </Typography.Text>
            ))}
        </Flex>
      </Flex>
    </>
  );
};
